import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import moment from 'moment-timezone'
import { Calendar, Clock, Users, GraduationCap, ArrowRight, CalendarX } from 'lucide-react'
import axios from 'axios'

import { Drawer, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from '../../ui/drawer'
import { Button } from '../../ui/button'
import { Badge } from '../../ui/badge'
import Avatar from '../Avatar/Avatar'
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner'
import { requestCreator } from '../../../utilities/requests'
import { hasDistinctStudentLegalName, studentDisplayName } from '../../../utilities/studentNames'

const { get: getKlassDay, cancel: cancelKlassDayRequest } = requestCreator()

function EventDrawer({
  open,
  onClose,
  klassId = null,
  date = null,
  time = null,
  facilityId = null,
  timeZone = 'UTC',
  onViewFullClass = null
}) {
  const [klassDay, setKlassDay] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (open && klassId && date && time) {
      setIsLoading(true)
      setError(false)
      getKlassDay(
        `/api/classes/${klassId}/class_days/${moment(date).tz(timeZone).format('YYYY-MM-DD')}/${moment(date)
          .tz(timeZone)
          .format('HH:mm')}`
      )
        .then((data) => {
          setKlassDay(data)
          setIsLoading(false)
        })
        .catch((err) => {
          if (!axios.isCancel(err)) {
            setIsLoading(false)
            setError(true)
          }
        })
    }

    return () => {
      cancelKlassDayRequest()
    }
  }, [open, klassId, date, time, timeZone, facilityId])

  function handleClose() {
    setKlassDay(null)
    setError(false)
    onClose()
  }

  function renderContent() {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center p-12">
          <LoadingSpinner size="1/5" />
        </div>
      )
    }

    if (error || !klassDay) {
      return (
        <>
          <div className="flex flex-col items-center justify-center flex-1 p-12 text-center">
            <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-gray-100">
              <CalendarX className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">
              {error ? 'Unable to Load Class' : 'No Class Information'}
            </h3>
            <p className="text-sm text-gray-500 max-w-sm">
              {error
                ? 'There was an error loading the class details. Please try again or contact support if the problem persists.'
                : 'No information is available for this class at the moment.'}
            </p>
          </div>
          <DrawerFooter className="border-t border-gray-200">
            <Button variant="outline" onClick={handleClose} size="lg">
              Close
            </Button>
          </DrawerFooter>
        </>
      )
    }

    const { coaches = [], students = [], skillLevels = [], startDatetime } = klassDay
    const formattedDate = moment(startDatetime).tz(timeZone).format('dddd, MMMM D, YYYY')
    const formattedTime = moment(startDatetime).tz(timeZone).format('h:mm A')
    const sortedStudents = students
      .slice()
      .sort((a, b) => studentDisplayName(a).localeCompare(studentDisplayName(b), 'en', { sensitivity: 'base' }))
    const viewFullClassUrl = `/facilities/${facilityId}/classes/${klassId}/class_days/${moment(date)
      .tz(timeZone)
      .format('YYYY-MM-DD')}/${moment(date).tz(timeZone).format('HH:mm')}`

    return (
      <>
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 p-2 bg-gray-100 rounded-lg">
                <Calendar className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Date</p>
                <p className="text-base font-semibold text-gray-900">{formattedDate}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 p-2 bg-gray-100 rounded-lg">
                <Clock className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Time</p>
                <p className="text-base font-semibold text-gray-900">{formattedTime}</p>
              </div>
            </div>
          </div>

          {coaches && coaches.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-gray-600" />
                <h3 className="font-semibold text-gray-900">{coaches.length === 1 ? 'Coach' : 'Coaches'}</h3>
              </div>
              <div className="flex flex-wrap gap-3">
                {coaches.map((coach) => (
                  <div key={coach.id} className="flex items-center gap-2">
                    <Avatar
                      fullName={coach.fullName || coach.preferredName}
                      cloudinaryPhotoPublicId={coach.cloudinaryPhotoPublicId}
                      alt={`Photo of ${coach.fullName || coach.preferredName}`}
                      userId={coach.id}
                      size={5}
                      className="border-2 border-white shadow-sm"
                    />
                    <span className="text-sm font-medium text-gray-700">{coach.preferredName || coach.fullName}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {skillLevels && skillLevels.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-gray-600" />
                <h3 className="font-semibold text-gray-900">Skill Levels</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {skillLevels.map((level, index) => (
                  <Badge key={level.id || level.name || index} variant="secondary">
                    {level.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {students && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-gray-600" />
                <h3 className="font-semibold text-gray-900">Students ({students.length})</h3>
              </div>
              {students.length === 0 ? (
                <p className="text-sm text-gray-500">No students enrolled</p>
              ) : (
                <div className="space-y-1">
                  {sortedStudents.map((student) => (
                    <div key={student.id} className="flex items-center gap-3 py-1.5">
                      <Avatar
                        fullName={studentDisplayName(student)}
                        cloudinaryPhotoPublicId={student.cloudinaryPhotoPublicId}
                        alt={`Photo of ${studentDisplayName(student)}`}
                        userId={student.id}
                        size={8}
                        isStudent
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{studentDisplayName(student)}</p>
                        {hasDistinctStudentLegalName(student) && (
                          <p className="text-xs text-gray-500">{student.fullName}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DrawerFooter className="border-t border-gray-200">
          {onViewFullClass ? (
            <Button className="w-full" size="lg" type="button" onClick={onViewFullClass}>
              View Full Class
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Link to={viewFullClassUrl} className="w-full">
              <Button className="w-full" size="lg">
                View Full Class
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          )}
          <Button variant="outline" onClick={handleClose} size="lg">
            Close
          </Button>
        </DrawerFooter>
      </>
    )
  }

  return (
    <Drawer open={open} onOpenChange={(isOpen) => !isOpen && handleClose()} direction="right">
      <DrawerContent className="max-h-[96vh] md:max-h-screen">
        <DrawerHeader className="border-b border-gray-200">
          <DrawerTitle className="text-xl text-gray-900">Class Details</DrawerTitle>
          <DrawerDescription>View class information and attendance</DrawerDescription>
        </DrawerHeader>
        {renderContent()}
      </DrawerContent>
    </Drawer>
  )
}

EventDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  klassId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  date: PropTypes.object,
  time: PropTypes.string,
  facilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  timeZone: PropTypes.string,
  onViewFullClass: PropTypes.func
}

export default EventDrawer
